import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';

import cn from '@/utils/cn';

// Custom-rendered dropdown (Radix Select) instead of a native <select>.
//
// THIS IS THE ONLY WAY TO GET A ROUNDED OPTION LIST. A native <select>'s open
// list is painted by the OS/browser, not by the page: no stylesheet can round
// its corners, tint its rows or change its font. Rounding the closed box and
// leaving the popup square is exactly the mismatch this component exists to
// remove, so prefer it for every dropdown in the app.
//
// Keeps the native-<select> call signature (value / onChange / `<option>`
// children / `options` prop) so moving a call site here is an import change.
// Inside a react-hook-form form, drive it with `Controller` - it is a button,
// not an input, so `register()`'s uncontrolled ref has no value to read.
// See design.md.
export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onChange?: (e: { target: { value: string; name?: string } }) => void;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  /**
   * Alternative to `<option>` children, matching the native form `Select`'s
   * `options` prop so a call site can move here by changing only its import.
   */
  options?: SelectOption[];
  /** Shown on the trigger while nothing is selected. */
  placeholder?: string;
  /** Marks the trigger invalid (red border + aria-invalid) for form use. */
  error?: boolean;
  /** Forwarded onto the change event, which react-hook-form's Controller reads. */
  name?: string;
  /**
   * Accessible name for the trigger. Declared explicitly because TypeScript does not
   * excess-property-check hyphenated JSX attributes: callers were already passing
   * `aria-label` and it was being silently dropped, leaving the button unnamed.
   */
  'aria-label'?: string;
}

// @radix-ui/react-select reserves the empty string: "" is how the primitive
// expresses "nothing selected" (shouldShowPlaceholder), so an item with
// value="" can be picked but never labels the trigger - selecting "All roles"
// leaves the closed select blank. Native <select> has no such rule, and this
// component keeps the native call signature, so callers legitimately write
// `<option value="">All roles</option>` for a cleared filter. Swap "" for a
// private sentinel on the way into Radix and back again on the way out, so ""
// stays the value callers see on both the `value` prop and the onChange event.
const EMPTY_VALUE = '__empty__';
const toRadix = (value: string): string => (value === '' ? EMPTY_VALUE : value);
const fromRadix = (value: string): string => (value === EMPTY_VALUE ? '' : value);

interface OptionElement {
  // `disabled` is forwarded to the Radix item, so `<option disabled>` grays an entry
  // out and makes it unselectable exactly as it would on a native select.
  props: { value: string | number; children?: React.ReactNode; disabled?: boolean };
}

export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      disabled,
      className,
      children,
      options: optionList,
      placeholder,
      error,
      name,
      'aria-label': ariaLabel,
    },
    ref,
  ) => {
    // `options` and children are interchangeable; the prop wins when both are
    // given, because a caller passing it meant it.
    const options: OptionElement[] = optionList
      ? optionList.map((o) => ({ props: { value: o.value, children: o.label } }))
      : (React.Children.toArray(children) as unknown as OptionElement[]);

    // Radix labels the closed trigger by portalling the SELECTED ITEM's text
    // into it, so a value with no matching item leaves the box silently blank -
    // it looks unset even though the form holds a value, and saving from that
    // screen looks like it lost the setting.
    //
    // A value can fail to match for reasons the wrapper cannot police: a stored
    // '04' against an option list keyed '4', a code retired from the catalogue
    // since it was saved, or options that arrive a render after the value does.
    // Showing the raw value is not pretty, but it is honest and visible, which
    // an empty box is not.
    const selected = value === undefined ? undefined : toRadix(value);
    const hasMatchingOption =
      selected === undefined ||
      options.some((opt) => toRadix(String(opt.props.value)) === selected);
    const unmatchedLabel = !hasMatchingOption && value ? value : undefined;

    return (
      <SelectPrimitive.Root
        value={value === undefined ? undefined : toRadix(value)}
        defaultValue={defaultValue === undefined ? undefined : toRadix(defaultValue)}
        onValueChange={(v) => onChange?.({ target: { value: fromRadix(v), name } })}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger
          ref={ref}
          aria-label={ariaLabel}
          aria-invalid={error || undefined}
          className={cn(
            'flex h-11 w-full items-center justify-between gap-2 overflow-hidden rounded-xl border bg-background px-3 text-base shadow-sm sm:h-9 sm:text-sm',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'data-[placeholder]:text-muted-foreground',
            error ? 'border-danger focus-visible:ring-danger' : 'border-input',
            className,
          )}
        >
          <span className="min-w-0 truncate text-left">
            {/*
              Children are passed ONLY for the unmatched case. Radix disables
              the item-text portal whenever SelectValue has children
              (valueNodeHasChildren), so passing one unconditionally would blank
              every normally-selected value - the opposite bug.
            */}
            {unmatchedLabel === undefined ? (
              <SelectPrimitive.Value placeholder={placeholder} />
            ) : (
              <SelectPrimitive.Value placeholder={placeholder}>
                {unmatchedLabel}
              </SelectPrimitive.Value>
            )}
          </span>
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            // `bg-white`, not `bg-popover`: the token resolves to white today,
            // but the panel being white is a stated design rule rather than a
            // coincidence of the current palette, so it is pinned here.
            //
            // z-[70] IS DELIBERATE AND HAS TO STAY ABOVE THE OTHER LAYERS.
            // A select is never the bottom of the stack: it is always opened
            // from inside something else, so it has to paint above whatever that
            // is. The scale in use here:
            //
            //   z-50   dialog, sheet, popover, dropdown-menu, toast
            //   z-[60] the DatePicker popover (raised so the calendar clears a
            //          dialog it is opened from) and the skip-link
            //   z-[70] this menu
            //   z-[100] the MFD sync overlay, which blocks the whole page
            //
            // At z-50 the month and year menus rendered BEHIND the calendar that
            // contains them - the list opened, and was invisible.
            className="z-[70] max-h-[var(--radix-select-content-available-height)] min-w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-white text-popover-foreground shadow-lg"
            position="popper"
            sideOffset={4}
            collisionPadding={8}
          >
            <SelectPrimitive.Viewport className="max-h-[min(300px,var(--radix-select-content-available-height))] overflow-y-auto overscroll-contain p-1">
              {options.map((opt) => (
                <SelectPrimitive.Item
                  key={opt.props.value}
                  value={toRadix(String(opt.props.value))}
                  disabled={opt.props.disabled}
                  className={cn(
                    'flex min-h-11 w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2 py-2.5 text-sm outline-none sm:min-h-0 sm:py-1.5',
                    // Pointing at a row vs. the row that IS the current value are
                    // different states and read differently: hover is the pale
                    // accent tint, the chosen option is filled with the brand
                    // colour - the same "this one is active" treatment the
                    // DatePicker's selected day and the segmented toggles use.
                    'focus:bg-accent focus:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
                    'data-[state=checked]:bg-primary data-[state=checked]:font-semibold data-[state=checked]:text-primary-foreground',
                    // Two-variant selector, so it beats both singles above on
                    // specificity rather than relying on class order: hovering
                    // the already-selected row must not repaint it pale.
                    'data-[state=checked]:data-[highlighted]:bg-brand-hover data-[state=checked]:data-[highlighted]:text-white',
                    'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
                  )}
                >
                  <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    <SelectPrimitive.ItemIndicator>
                      <Check className="h-3.5 w-3.5" />
                    </SelectPrimitive.ItemIndicator>
                  </span>
                  <SelectPrimitive.ItemText>{opt.props.children}</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    );
  },
);
Select.displayName = 'SelectMenu';
